import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLicense } from './LicenseContext';

export interface SiteNode {
  id: string;
  name: string;
  address: string;
  timezone: string;
  status: 'online' | 'degraded' | 'offline';
  alarmCount: number;
  cameraCount: number;
  cameraOnline: number;
  parentId?: string;
}

// Demo org only — fake sites
const DEMO_SITES: SiteNode[] = [
  { id:'th',  name:'Town Hall',         address:'483 George St, Sydney NSW 2000',             timezone:'Australia/Sydney', status:'online',   alarmCount:1, cameraCount:12, cameraOnline:12 },
  { id:'cc',  name:'Civic Centre',      address:'1 Civic Pl, Parramatta NSW 2150',            timezone:'Australia/Sydney', status:'online',   alarmCount:1, cameraCount:10, cameraOnline:10 },
  { id:'aq',  name:'Aquatic Centre',    address:'1 Aquatic Dr, Homebush NSW 2127',            timezone:'Australia/Sydney', status:'degraded', alarmCount:0, cameraCount:8,  cameraOnline:6  },
  { id:'cd',  name:'Council Depot',     address:'22 Industrial Rd, Wetherill Park NSW 2164',  timezone:'Australia/Sydney', status:'online',   alarmCount:0, cameraCount:6,  cameraOnline:6  },
  { id:'ps',  name:'Parramatta Square', address:'10 Darcy St, Parramatta NSW 2150',           timezone:'Australia/Sydney', status:'online',   alarmCount:0, cameraCount:12, cameraOnline:12 },
  { id:'rw',  name:'River Walk',        address:'Parramatta River Walk, Parramatta NSW 2150', timezone:'Australia/Sydney', status:'offline',  alarmCount:0, cameraCount:4,  cameraOnline:0  },
];

// Keep exported for any legacy references
export const ALL_SITES = DEMO_SITES;

interface SiteCtxValue {
  sites: SiteNode[];
  activeSiteId: string;
  activeSite: SiteNode | null;
  setActiveSiteId: (id: string) => void;
  sitesLoading: boolean;
}

const SiteCtx = createContext<SiteCtxValue>({
  sites: [],
  activeSiteId: 'all',
  activeSite: null,
  setActiveSiteId: () => {},
  sitesLoading: false,
});

export function SiteProvider({ children }: { children: React.ReactNode }) {
  const { isDemoOrg, isProtegeOrg } = useLicense();
  const [sites, setSites] = useState<SiteNode[]>([]);
  const [activeSiteId, setActiveSiteId] = useState<string>('all');
  const [sitesLoading, setSitesLoading] = useState(false);

  useEffect(() => {
    // Reset site selection when org changes
    setActiveSiteId('all');

    if (isDemoOrg) {
      setSites(DEMO_SITES);
      return;
    }

    if (isProtegeOrg) {
      // Fetch live sites from Protege GX
      setSitesLoading(true);
      const token = localStorage.getItem('lemai_token') || '';
      fetch('/api/protege/sites', {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.sites?.length) {
            const converted: SiteNode[] = data.sites.map((s: any) => ({
              id:           String(s.id),
              name:         s.name || `Site ${s.id}`,
              address:      s.name2 || '',
              timezone:     'Australia/Sydney',
              status:       'online' as const,
              alarmCount:   0,
              cameraCount:  0,
              cameraOnline: 0,
            }));
            setSites(converted);
          } else {
            setSites([]);
          }
        })
        .catch(() => setSites([]))
        .finally(() => setSitesLoading(false));
      return;
    }

    // All other orgs — no sites
    setSites([]);
  }, [isDemoOrg, isProtegeOrg]);

  const activeSite = activeSiteId === 'all'
    ? null
    : sites.find(s => s.id === activeSiteId) ?? null;

  return (
    <SiteCtx.Provider value={{ sites, activeSiteId, activeSite, setActiveSiteId, sitesLoading }}>
      {children}
    </SiteCtx.Provider>
  );
}

export function useSite() { return useContext(SiteCtx); }
